import { GoogleAnalyticsProvider } from './providers/googleProvider';

class AnalyticsManager {
  constructor() {
    this.providers = new Map();
    this.initialized = false;
    this.debug = process.env.REACT_APP_ANALYTICS_DEBUG === 'true';
    this.eventQueue = [];
    this.pageViewQueue = [];
  }

  async initialize(config = {}) {
    if (this.initialized) return;

    try {
      // Initialize Google Analytics if measurement ID is provided
      if (config.googleAnalytics?.measurementId) {
        const gaProvider = new GoogleAnalyticsProvider({
          measurementId: config.googleAnalytics.measurementId,
          sampleRate: config.googleAnalytics.sampleRate,
          anonymizeIp: config.googleAnalytics.anonymizeIp,
          respectDoNotTrack: config.googleAnalytics.respectDoNotTrack,
          debug: config.googleAnalytics.debug || this.debug
        });
        await gaProvider.initialize();
        this.providers.set('google', gaProvider);
      }

      this.initialized = true;

      // Process any queued events after initialization
      await this.processQueuedEvents();
    } catch (error) {
      console.error('[AnalyticsManager] Initialization failed:', error);
      throw error;
    }
  }

  async processQueuedEvents() {
    // Process queued page views
    for (const path of this.pageViewQueue) {
      await this.trackPage(path);
    }
    this.pageViewQueue = [];

    // Process queued events
    for (const { eventName, eventData } of this.eventQueue) {
      await this.track(eventName, eventData);
    }
    this.eventQueue = [];
  }

  async track(eventName, eventData = {}) {
    if (!this.initialized) {
      this.eventQueue.push({ eventName, eventData });
      return;
    }

    const promises = Array.from(this.providers.values()).map(provider =>
      provider.track(eventName, eventData)
    );

    try {
      await Promise.all(promises);
    } catch (error) {
      console.error('[AnalyticsManager] Failed to track event:', error);
    }
  }

  async trackBatch(events) {
    if (!this.initialized) {
      this.eventQueue.push(...events.map(e => ({ eventName: e.name, eventData: e.data })));
      return;
    }

    const promises = Array.from(this.providers.values()).map(provider =>
      provider.trackBatch(events)
    );

    try {
      await Promise.all(promises);
    } catch (error) {
      console.error('[AnalyticsManager] Failed to track batch events:', error);
    }
  }

  async trackPage(path) {
    if (!this.initialized) {
      this.pageViewQueue.push(path);
      return;
    }

    const promises = Array.from(this.providers.values()).map(provider =>
      provider.trackPage(path)
    );

    try {
      await Promise.all(promises);
    } catch (error) {
      console.error('[AnalyticsManager] Failed to track page view:', error);
    }
  }

  getProvider(name) {
    return this.providers.get(name);
  }

  hasProvider(name) {
    return this.providers.has(name);
  }
}

// Create a singleton instance
const analyticsManager = new AnalyticsManager();
export default analyticsManager; 